/**
 * @fileoverview Neutral pill rendering a tag's name, with an optional remove button.
 * @module features/tags/components/TagChip
 */
import { type ReactElement } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { Tag } from '../schemas/tagSchema'

interface TagChipProps {
  tag: Pick<Tag, 'name'>
  className?: string
  /** When set, renders a remove button inside the pill, revealed on hover/focus. */
  onRemove?: () => void
  removeDisabled?: boolean
  removeLabel?: string
}

export function TagChip({
  tag,
  className,
  onRemove,
  removeDisabled,
  removeLabel,
}: TagChipProps): ReactElement {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border border-input bg-muted px-2.5 py-0.5 text-xs font-medium text-foreground',
        onRemove && 'group gap-1 pr-1.5',
        className
      )}
    >
      {tag.name}
      {onRemove && (
        <button
          type="button"
          aria-label={removeLabel}
          onClick={onRemove}
          disabled={removeDisabled}
          className="opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 hover:text-destructive"
        >
          <X className="size-3" />
        </button>
      )}
    </span>
  )
}
